import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TABLES } from '../../../../../Apis/Tables';
import { MODELS } from '../../../../../Apis/Models';
import { clearSearchFilters } from '../../../../../reduxToolkit/Search/SearchSlice';
import { EmailFrequency } from '../../../../../enums/EmailFrecuency';
import {permissionObj} from "../../../../../Helpers/permission";
import {
    ATTENDANCE_COLOR_MODEL,
    ATTENDANCE_SUBVIEW_MODEL,
    TABS_ATTENDANCE
} from "../Model/Model";
import {useLocation, useNavigate} from 'react-router-dom';
import routes from '../../../../../Routes/routes';
import {VIEW_TYPE} from '../../../../../enums/ViewType';
import {GetAttendanceColor} from '../../../../../reduxToolkit/AttendanceConfig/AttendanceConfigApi';

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * Custom hook to manage payroll/attendance module functionality.
 *
 * Handles:
 * - Tab management based on permissions
 * - Context initialization
 * - Filtering, exporting, and simple search
 * - State management for module variables like view, color, steps, and active tab
 *
 *
 */
const PayrollFunctionality = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const currentTab = location?.state?.currentTab;
    const currentSubView = location?.state?.currentSubView;

    const actionsRef = useRef({});

    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const [subView, setSubView] = useState(null); // Variable to manage subviews and permissions. When entering the module, it is necessary to initialize the variable (if applicable, used SUB_VIEW_MODEL).
    const [filterDialogShow, setFilterDialogShow] = useState(false);
    const [finalArray, setFinalArray] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [selectedOption, setSelectedOption] = useState(EmailFrequency.DAILY.id);
    const [bodyForExport, setBodyForExport] = useState('');
    const [searchElement, setSearchElement] = useState('');
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [search, setSearch] = useState(false);
    const [searchClean, setSearchClean] = useState(false);
    const [exportAttendance, setExportAttendance] = useState(true);
    const [exportAttendanceAdvance, setExportAttendanceAdvance] = useState(true);
    const [modified, setModified] = useState(false);
    const [criteria, setCriteria] = useState();
    const [sendSimpleSearch, setSendSimpleSearch] = useState(false);
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));
    const [render, setRender] = useState('');

    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);

    const [selectForDelete, setSelectForDelete] = useState([]);

    const [view, setView] = useState(VIEW_TYPE.LIST.name);
    const [moduleId, setModuleId] = useState(MODELS.Attendance);
    const [option, setOption] = useState(TABLES.ATTENDANCE);

    const [color, setColor] = useState({...ATTENDANCE_COLOR_MODEL.setDefaultData()});

    const MANAGE_SUBVIEWS_BY_PERMISSION = [
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS,
            permission: permissionObj.WEB_ATTENDANCE_SHIFT_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.SETTINGS,
            permission: permissionObj.WEB_ATTENDANCE_SETTING_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEPARTMENT_POLICIES,
            permission: permissionObj.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.GEOFENCE,
            permission: permissionObj.WEB_ATTENDANCE_READ,
        },
    ];
    // Maps Submissions sub-views to the required permissions.
    const SUBMISSIONS_SUBVIEWS_BY_PERMISSION = [
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW,
            permission: permissionObj.WEB_ATTENDANCE_SHIFT_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUEST,
            permission: permissionObj.WEB_ATTENDANCE_SETTING_READ,
        },
        {
            subView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.HISTORY,
            permission: permissionObj.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ,
        },
    ];

    useLayoutEffect(() => {
        if(currentTab && currentSubView){
            setActiveStep(currentTab);
            setSubView(currentSubView);
            setSelectedOption(EmailFrequency.DAILY.id)
            handleStepChange(currentTab);
        }
    }, [location]);

    useEffect(() => {
        setLoaded(true);
        return () => {
            dispatch(clearSearchFilters());
        };
    }, []);

    /**
     * Hook to display the tabs if the user has the corresponding permissions.
     */
    useLayoutEffect(() => {
        let activeTab = null;
        const stepConfig = [
            {
                tab: TABS_ATTENDANCE.ATTENDANCE.TRANS,
                perm: [permissionObj?.WEB_ATTENDANCE_READ],
                index: TABS_ATTENDANCE.ATTENDANCE.INDEX,
            },
        /*    {
                tab: TABS_ATTENDANCE.SUBMISSIONS.TRANS,
                perm:  [
                    permissionObj?.WEB_ATTENDANCE_SHIFT_READ,
                    permissionObj?.WEB_ATTENDANCE_SETTING_READ,
                    permissionObj?.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ,
                ],
                index: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
            },*/
            {
                tab: TABS_ATTENDANCE.MANAGE.TRANS,
                perm: [
                    permissionObj?.WEB_ATTENDANCE_SHIFT_READ,
                    permissionObj?.WEB_ATTENDANCE_SETTING_READ,
                    permissionObj?.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ,
                ],
                index: TABS_ATTENDANCE.MANAGE.INDEX,
            },
        ];

        const auxStep = stepConfig.reduce((acc, { tab, perm, index }) => {
            const hasPermission = DEV || perm.some(p => permission?.includes(p));
            if (hasPermission) {
                acc.push({ label: t(tab), index: index });
                if (activeTab === null) activeTab = index;
            }
            return acc;
        }, []);

        setSteps(auxStep);

        if (activeTab !== null && !currentTab) handleStepChange(activeTab);
    }, [t, permission]);

    useEffect(() => {
        setSelectForDelete([])
    }, [activeStep, view, subView]);

    useEffect(() => {
        if(activeStep === TABS_ATTENDANCE.ATTENDANCE.INDEX){
            dispatch(GetAttendanceColor()).then(({payload: {data: { data }}}) => {
                setColor({ ...ATTENDANCE_COLOR_MODEL, ...data })
            });
        }
    }, [activeStep]);

    const handleView = (view) => {
        clear();
        setView(view);
    }

    const handleSubView = (step) => {
        if (currentSubView != null) {
            setSubView(currentSubView);
            return;
        }
        switch (step) {
            case TABS_ATTENDANCE.MANAGE.INDEX: {
                const allowedSubView = MANAGE_SUBVIEWS_BY_PERMISSION.find(
                    ({ permission: perm }) => DEV || permission?.includes(perm),
                );
                setSubView(allowedSubView?.subView ?? null);
                break;
            }
            case TABS_ATTENDANCE.SUBMISSIONS.INDEX: {
                const allowedSubView = SUBMISSIONS_SUBVIEWS_BY_PERMISSION.find(
                    ({ permission: perm }) => DEV || permission?.includes(perm),
                );
                setSubView(allowedSubView?.subView ?? null);
                break;
            }
            default:
                setSubView(null);
                break;
        }
    };

    const handleModuleOption = (step) => {
        switch (step) {
            case TABS_ATTENDANCE.ATTENDANCE.INDEX: {
                setModuleId(MODELS.Attendance);
                setOption(TABLES.ATTENDANCE);
                break;
            }
            case TABS_ATTENDANCE.SUBMISSIONS.INDEX: {
                setModuleId(MODELS.Attendance);
                setOption(TABLES.ATTENDANCE);
                break;
            }
            case TABS_ATTENDANCE.MANAGE.INDEX: {
                setOption(TABLES.ATTENDANCE_SHIFT);
                break;
            }
            default: {
                setModuleId(null);
                setOption(null);
                break;
            }
        }
    };


    /**
     * The index of the active tab is stored in the context, so that the user can return to the same tab in case the view is redirected.
     * @param step
     */
    const handleStepChange = (step) => {
        clear();
        setActiveStep(step);
        setSelectedOption(EmailFrequency.DAILY.id);
        navigate(location.pathname, { replace: true, state: {} });
        handleSubView(step);
        handleModuleOption(step);
    };

    const clear = () => {
        setSearchElement('');
        setFinalArray([]);
        setSearch(false);
    }
    /**
     * Function to validate permissions for the create and delete buttons. It can also be applied to import, export, and search buttons.
     * If no permission is assigned or the tabIndex variable from the Context is null, the button is import { ACTION_MODEL } from './../../../../Helpers/ActionModel';
     not displayed.
     * @param array list: List of permissions based on the active tab.
     * @returns {boolean|*}
     */
    const permissionButtons = (list) => {
        if(activeStep == null){
            return false;
        }
        const permissionRequired = list[activeStep];

        if (!permissionRequired) {
            return false;
        }
        return DEV || permission?.includes(permissionRequired);
    };

    const goCreate = () => {
        if (!TABS_ATTENDANCE.MANAGE) return;

        if(subView === ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEPARTMENT_POLICIES){
            handleCreate();
            return;
        }

        const routeBySubView = {
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS]: routes.attendanceShiftAccess.add,
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.SETTINGS]: "",
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEPARTMENT_POLICIES]: "",
            [ATTENDANCE_SUBVIEW_MODEL.MANAGE.GEOFENCE]: routes.attendanceGeoFence.create,
            [ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW]:"",
            [ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.REQUEST]: "",
            [ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.HISTORY]: "",
        };

        const route = routeBySubView[subView];
        if (route) {
            navigate(route, {
                state: {
                    currentTab: activeStep,
                    currentSubView: subView,
                },
            });
        }
    };


    const changeSubViewManage = (value) => {
        setSearchElement('');
        setSubView(value)
    }

    const registerAction = (key, actions) => {
        actionsRef.current[key] = actions;
    };

    const handleDelete = () => {
        actionsRef.current?.[subView]?.handleDelete();
    };

    const handleCreate = () => {
        actionsRef.current?.[subView]?.handleCreate();
    };

    const handleUpdate = () => {
        actionsRef.current?.[subView]?.handleUpdate();
    };

    return {
        searchElement,
        setSearchElement,
        option,
        selectedOption,
        setSimpleSearch,
        setSearch,
        contractorId,
        supplierId,
        search,
        setSendSimpleSearch,
        searchClean,
        criteria,
        setCriteria,
        filterDialogShow,
        setSelectedOption,
        exportAttendance,
        exportAttendanceAdvance,
        finalArray,
        moduleId,
        subView,
        activeStep,
        setActiveStep,
        steps,
        setFilterDialogShow,
        setFinalArray,
        handleStepChange,
        view,
        setView,
        render,
        setRender,
        color,
        permissionButtons,
        handleUpdate,
        setExportAttendance,
        bodyForExport,
        setBodyForExport,
        changeSubViewManage,
        goCreate,
        selectForDelete, setSelectForDelete,
        registerAction,
        handleDelete,
        modified,
        setModified,
        handleView,
        DEV,
        permission,
        navigate
    };
};

export default PayrollFunctionality;
